import { useMemo, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AnalyticsApi from 'libs/api/analytics';
import useFilters from '../useFilters';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import _ from 'lodash';
import { Table } from 'antd';
import { fileNameEdit } from 'helpers/FileUtils';
import Text from 'antd/es/typography/Text';
import { usePdf } from '../../../../hooks/usePdf';

const api = new AnalyticsApi('charts/consumi_pieces/');

const format = (v) => `${v}%`;

const defaultState = {
  table1: [],
  table2: [],
  total1: {},
  total2: {},
  barChart: [],
  total: {},
  tableData: [],
  barChangeLine: [],
  keys: [],
  bar2: {
    barChart: [],
    barChangeLine: [],
    tableData: [],
    maxValue: 0,
  },
};

const useInvoicesCategoryPieces = (isPrint, allReportsData, isAllReports) => {
  const { t } = useTranslation();
  const { filters, title, searchParams } = useFilters(
    false,
    false,
    isAllReports,
  );
  const { handleDownloadPdf, loadingPdf, createCustomEvent } = usePdf();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultState);
  const [errors, setErrors] = useState([]);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const [columns, setColumns] = useState([
    {
      title: t('CATEGORIA MERCEOLOGICA'),
      dataIndex: 'Pezzi',
      fixed: true,
    },
    {
      title: t('Pezzi YTD'),
      dataIndex: 'Pezzi YTD',
      render: (v) => Helpers.numberWithCommas(v),
    },
    {
      title: t('Pezzi LY'),
      dataIndex: 'Pezzi LY',
      render: (v) => Helpers.numberWithCommas(v),
    },
    {
      title: t('Pezzi VS LY +/-(%)'),
      dataIndex: 'Pezzi VS LY +/-(%)',
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('Inc. % Pezzi YTD'),
      dataIndex: 'Inc. % Pezzi YTD',
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('Inc. % Pezzi LY'),
      dataIndex: 'Inc. % Pezzi LY',
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
  ]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (!params.by_period || !params.by_shopping_center) return;

        let table1, table2;

        if (!isAllReports) {
          const {
            data: { table1: backTable1, table2: backTable2 },
          } = await api.getFootfalls({ params });
          table1 = backTable1;
          table2 = backTable2;
        } else {
          const { table1: backTable1, table2: backTable2 } = allReportsData;
          table1 = backTable1;
          table2 = backTable2;
        }

        if (!table1 || !table2) return;

        const maxValueFroLy1 = _.maxBy(table1.data, 'Inc. % Pezzi YTD')[
          'Inc. % Pezzi YTD'
        ];
        const maxValueFroYtd1 = _.maxBy(table1.data, 'Inc. % Pezzi LY')[
          'Inc. % Pezzi LY'
        ];

        const maxValue1 =
          maxValueFroLy1 > maxValueFroYtd1 ? maxValueFroLy1 : maxValueFroYtd1;

        const maxValueFroLy2 = _.maxBy(table2.data, 'Inc. % Pezzi YTD')[
          'Inc. % Pezzi YTD'
        ];
        const maxValueFroYtd2 = _.maxBy(table2.data, 'Inc. % Pezzi LY')[
          'Inc. % Pezzi LY'
        ];

        const maxValue2 =
          maxValueFroLy2 > maxValueFroYtd2 ? maxValueFroLy2 : maxValueFroYtd2;

        const barChart = table1.data.map((item) => ({
          date: item['Pezzi'],
          'Inc. % Pezzi YTD': item['Inc. % Pezzi YTD'],
          'Inc. % Pezzi YTDColor': '#61cdbb',
          'Inc. % Pezzi LY': item['Inc. % Pezzi LY'],
          'Inc. % Pezzi LYColor': '#f47560',
        }));

        const barChangeLine = {
          id: 'Pezzi VS LY +/-(%)',
          data: table1.data.map((item) => ({
            x: item['Pezzi'],
            y: item['Pezzi VS LY +/-(%)'],
            tooltipKeys: [
              {
                key: 'Inc. % Pezzi YTD',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(format(item['Inc. % Pezzi YTD'])),
                ),
                color: '#61cdbb',
                percent: '',
              },
              {
                key: 'Inc. % Pezzi LY',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(format(item['Inc. % Pezzi LY'])),
                ),
                color: '#f47560',
                percent: '',
              },
              {
                key: 'Pezzi VS LY +/-(%)',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(format(item['Pezzi VS LY +/-(%)'])),
                ),
                color: '#1f77b4',
                percent: '',
              },
            ],
          })),
        };

        const bar2Chart2 = table2.data.map((item) => ({
          date: item['Pezzi'],
          'Inc. % Pezzi YTD': item['Inc. % Pezzi YTD'],
          'Inc. % Pezzi YTDColor': '#61cdbb',
          'Inc. % Pezzi LY': item['Inc. % Pezzi LY'],
          'Inc. % Pezzi LYColor': '#f47560',
        }));

        const bar2ChangeLine = {
          id: 'Pezzi VS LY +/-(%)',
          data: table2.data.map((item) => ({
            x: item['Pezzi'],
            y: item['Pezzi VS LY +/-(%)'],
            tooltipKeys: [
              {
                key: 'Inc. % Pezzi YTD',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(format(item['Inc. % Pezzi YTD'])),
                ),
                color: '#61cdbb',
                percent: '',
              },
              {
                key: 'Inc. % Pezzi LY',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(format(item['Inc. % Pezzi LY'])),
                ),
                color: '#f47560',
                percent: '',
              },
              {
                key: 'Pezzi VS LY +/-(%)',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(format(item['Pezzi VS LY +/-(%)'])),
                ),
                color: '#1f77b4',
                percent: '',
              },
            ],
          })),
        };

        setData({
          table1: table1.data,
          table2: table2.data,
          total1: table1.total,
          total2: table2.total,
          maxValue: maxValue1,
          barChart,
          tableData: [],
          barChangeLine: [barChangeLine],
          bar2: {
            barChart: bar2Chart2,
            barChangeLine: [bar2ChangeLine],
            tableData: [],
            maxValue: maxValue2,
          },
        });
        setErrors([]);
      } catch (e) {
        console.log(e);
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setErrors(UIHelper.showAnalyticsError(e));
        setData(defaultState);
      } finally {
        setLoading(false);
        if (isPrint) {
          createCustomEvent();
        }
      }
    })();
  }, [searchParams, isPrint, allReportsData, isAllReports]);

  const columns2 = useMemo(() => {
    return [
      {
        title: t('Segmento'),
        dataIndex: 'Pezzi',
        fixed: true,
      },
      {
        title: t('Pezzi YTD'),
        dataIndex: 'Pezzi YTD',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('Pezzi LY'),
        dataIndex: 'Pezzi LY',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('Pezzi VS LY +/-(%)'),
        dataIndex: 'Pezzi VS LY +/-(%)',
        render: (v) =>
          Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
      },
      {
        title: t('Inc. % Pezzi YTD'),
        dataIndex: 'Inc. % Pezzi YTD',
        render: (v) =>
          Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
      },
      {
        title: t('Inc. % Pezzi LY'),
        dataIndex: 'Inc. % Pezzi LY',
        render: (v) =>
          Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
      },
    ];
  }, []);

  const summary = useCallback(() => {
    const { total1 = {} } = data;

    delete total1['Pezzi'];

    const tableKeys = Object.keys(total1);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total1[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data, columns]);

  const summary2 = useCallback(() => {
    const { total2 = {} } = data;

    delete total2['Pezzi'];

    const tableKeys = Object.keys(total2);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total2[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data]);

  return {
    columns2,
    columns,
    data,
    filters,
    format,
    title,
    loading,
    handleDownloadExcel,
    searchParams,
    summary,
    summary2,
    handleDownloadPdf,
    loadingPdf,
    errors,
  };
};

export default useInvoicesCategoryPieces;
